/**
 * Admin-Cockpit: Abendessen-Kopfzahlen, Location-Pflege, manuelle Anmeldung, Zwei-Wege-Mail.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Loader2,
  Mail,
  MapPin,
  Minus,
  Plus,
  Search,
  Trash2,
  UtensilsCrossed,
  Users,
} from 'lucide-react';
import {
  createEmptyDinnerDay,
  suggestDinnerLabelFromDate,
  useSeminarDinnerStore,
} from '../../store/seminarDinnerStore';
import {
  buildSeminarDinnerPublicUrl,
  enqueueDinnerInvitationMails,
  enqueueDinnerLocationDispatchMails,
  enqueueDinnerTestInvitationMail,
} from '../../services/seminarDinnerMailService';
import useAdminStore from '../../store/adminStore';
import type { SeminarDinnerDay } from '../../types/seminarDinner';

type ParticipantLike = {
  id?: string;
  email?: string;
  vorname?: string;
  nachname?: string;
  firma?: string;
};

interface SeminarDinnerManagerProps {
  seminarId: string;
  seminarTitle: string;
  /** Event-Teilnehmer für Phase-1-Einladung */
  participants?: ParticipantLike[];
}

const inputClass =
  'w-full rounded-lg bg-black/40 border border-slate-700 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#E6C299]';

const dinnerSeenStorageKey = (seminarId: string) => `pe_dinner_seen_at_${seminarId}`;

function loadDinnerSeenAt(seminarId: string): string {
  try {
    return localStorage.getItem(dinnerSeenStorageKey(seminarId)) || '';
  } catch {
    return '';
  }
}

function saveDinnerSeenAt(seminarId: string, iso: string): void {
  try {
    localStorage.setItem(dinnerSeenStorageKey(seminarId), iso);
  } catch {
    /* ignore */
  }
}

type ManualSource = 'team' | 'mandant' | 'extern';

/** teamId/clientId → tagId → count */
type PoolCounts = Record<string, Record<string, number>>;

const CountStepper: React.FC<{
  value: number;
  onChange: (n: number) => void;
  max?: number;
}> = ({ value, onChange, max = 50 }) => (
  <div className="flex items-center gap-0.5">
    <button
      type="button"
      onClick={() => onChange(Math.max(0, value - 1))}
      disabled={value <= 0}
      className="w-8 h-8 rounded-lg border border-slate-700 bg-slate-900 text-white flex items-center justify-center hover:border-[#E6C299] disabled:opacity-30"
    >
      <Minus className="w-3.5 h-3.5" />
    </button>
    <span className="w-8 text-center text-sm font-black tabular-nums text-[#E6C299]">{value}</span>
    <button
      type="button"
      onClick={() => onChange(Math.min(max, value + 1))}
      disabled={value >= max}
      className="w-8 h-8 rounded-lg border border-slate-700 bg-slate-900 text-white flex items-center justify-center hover:border-[#E6C299] disabled:opacity-30"
    >
      <Plus className="w-3.5 h-3.5" />
    </button>
  </div>
);

const SeminarDinnerManager: React.FC<SeminarDinnerManagerProps> = ({
  seminarId,
  seminarTitle,
  participants = [],
}) => {
  const {
    config,
    registrations,
    isLoading,
    subscribe,
    unsubscribe,
    saveConfig,
    setTage,
    updateDayLocation,
    addRegistration,
    updateRegistration,
    deleteRegistration,
    markInvitationMailsSent,
    markLocationMailsSent,
  } = useSeminarDinnerStore();

  const {
    crmClients,
    pendingApprovals,
    adminTeam,
    fetchClients,
    syncAdminTeam,
    isLoadingPending,
  } = useAdminStore();

  const [panelOpen, setPanelOpen] = useState(false);
  const [seenAt, setSeenAt] = useState(() => loadDinnerSeenAt(seminarId));
  const [showManual, setShowManual] = useState(false);
  const [manualSource, setManualSource] = useState<ManualSource>('team');
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualBemerkung, setManualBemerkung] = useState('');
  const [manualCounts, setManualCounts] = useState<Record<string, number>>({});
  const [teamCounts, setTeamCounts] = useState<PoolCounts>({});
  const [teamDirty, setTeamDirty] = useState(false);
  /** Entwurf aller Anmeldungen (Köpfe editieren / Abend abwählen / raus) */
  const [regDrafts, setRegDrafts] = useState<PoolCounts>({});
  const [regDirty, setRegDirty] = useState(false);
  const [mandantSearch, setMandantSearch] = useState('');
  const [selectedMandantId, setSelectedMandantId] = useState<string | null>(null);
  const [mandantCounts, setMandantCounts] = useState<Record<string, number>>({});
  const [testEmail, setTestEmail] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  /** Lokaler Entwurf der Abende (Datum/Label manuell) — speichern explizit */
  const [tageDraft, setTageDraft] = useState<SeminarDinnerDay[]>([]);

  useEffect(() => {
    if (!seminarId) return;
    subscribe(seminarId);
    setSeenAt(loadDinnerSeenAt(seminarId));
    setPanelOpen(false);
    return () => unsubscribe();
  }, [seminarId, subscribe, unsubscribe]);

  const unseenDinnerCount = useMemo(() => {
    return registrations.filter((r) => {
      const stamped = String(r.angemeldetAm || '').trim();
      if (!stamped) return !seenAt;
      if (!seenAt) return true;
      return stamped > seenAt;
    }).length;
  }, [registrations, seenAt]);

  const markDinnerSeen = () => {
    const now = new Date().toISOString();
    saveDinnerSeenAt(seminarId, now);
    setSeenAt(now);
  };

  const togglePanel = () => {
    setPanelOpen((prev) => {
      const next = !prev;
      if (next) markDinnerSeen();
      return next;
    });
  };

  useEffect(() => {
    if (!showManual) return;
    syncAdminTeam();
    if (!crmClients.length && !pendingApprovals.length) {
      void fetchClients();
    }
  }, [showManual, syncAdminTeam, fetchClients, crmClients.length, pendingApprovals.length]);

  const tageSyncKey = JSON.stringify(config?.tage || []);
  useEffect(() => {
    const parsed = (() => {
      try {
        return JSON.parse(tageSyncKey) as SeminarDinnerDay[];
      } catch {
        return [] as SeminarDinnerDay[];
      }
    })();
    setTageDraft(parsed.map((t) => ({ ...t })));
  }, [tageSyncKey]);

  const headcounts = useMemo(() => {
    const totals: Record<string, number> = {};
    (config?.tage || []).forEach((t) => {
      totals[t.tagId] = 0;
    });
    registrations.forEach((r) => {
      Object.entries(r.anmeldungen || {}).forEach(([tagId, n]) => {
        const v = Number(n) || 0;
        if (v <= 0) return;
        totals[tagId] = (totals[tagId] || 0) + v;
      });
    });
    return totals;
  }, [config?.tage, registrations]);

  const ensureConfig = async () => {
    if (config) return config;
    const next = {
      seminarId,
      titel: seminarTitle || 'Seminar',
      tage: [] as SeminarDinnerDay[],
    };
    await saveConfig(next);
    return next;
  };

  const handleFreischalten = async () => {
    setBusy('init');
    setStatusMsg(null);
    try {
      if (config) {
        setStatusMsg('Anmeldung ist bereits freigeschaltet — Abende unten manuell pflegen.');
        return;
      }
      await saveConfig({
        seminarId,
        titel: seminarTitle || 'Seminar',
        tage: [],
      });
      setStatusMsg(
        'Freigeschaltet. Mit „Abend hinzufügen“ Datum/Bezeichnung setzen, speichern, dann Einladung.'
      );
    } catch (err) {
      console.error('[SeminarDinnerManager] init:', err);
      setStatusMsg('Freischalten fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const handleAddDinnerDay = () => {
    setTageDraft((prev) => [...prev, createEmptyDinnerDay({ label: 'Abendessen' })]);
  };

  const handlePatchDinnerDay = (tagId: string, patch: Partial<SeminarDinnerDay>) => {
    setTageDraft((prev) =>
      prev.map((t) => {
        if (t.tagId !== tagId) return t;
        const next = { ...t, ...patch };
        // Label nur auto-vorschlagen, wenn leer oder noch der alte Datums-Vorschlag
        if (patch.datum != null) {
          const suggested = suggestDinnerLabelFromDate(patch.datum);
          const prevSuggested = suggestDinnerLabelFromDate(t.datum);
          if (suggested && (!t.label.trim() || t.label === prevSuggested || t.label === 'Abendessen')) {
            next.label = suggested;
          }
        }
        return next;
      })
    );
  };

  const handleRemoveDinnerDay = (tagId: string) => {
    if (!window.confirm('Diesen Abend entfernen?')) return;
    setTageDraft((prev) => prev.filter((t) => t.tagId !== tagId));
  };

  const handleSaveTage = async () => {
    setBusy('tage');
    setStatusMsg(null);
    try {
      await ensureConfig();
      const cleaned = tageDraft
        .map((t) => ({
          ...t,
          label: String(t.label || '').trim() || 'Abendessen',
          datum: String(t.datum || '').trim(),
        }))
        .filter((t) => t.datum || t.label);
      if (!cleaned.length) {
        setStatusMsg('Mindestens einen Abend mit Datum oder Bezeichnung eintragen.');
        return;
      }
      const missingDate = cleaned.filter((t) => !t.datum);
      if (missingDate.length) {
        setStatusMsg('Bitte für jeden Abend ein Datum setzen.');
        return;
      }
      await setTage(cleaned);
      setStatusMsg(`${cleaned.length} Abend(e) gespeichert.`);
    } catch (err) {
      console.error(err);
      setStatusMsg('Abende speichern fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const handleSaveDay = async (day: SeminarDinnerDay) => {
    setBusy(`day_${day.tagId}`);
    try {
      await updateDayLocation(day.tagId, {
        restaurantName: day.restaurantName || '',
        adresseTreffpunkt: day.adresseTreffpunkt || '',
        uhrzeit: day.uhrzeit || '',
      });
      setStatusMsg(`Location für „${day.label}“ gespeichert.`);
    } catch (err) {
      console.error(err);
      setStatusMsg('Speichern fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const clientPool = useMemo(() => {
    const map = new Map<string, { id: string; name: string; email: string; meta: string }>();
    [...crmClients, ...pendingApprovals].forEach((c) => {
      const email = String(c.email || '').trim().toLowerCase();
      if (!email.includes('@')) return;
      const name =
        [c.vorname, c.nachname].filter(Boolean).join(' ').trim() ||
        String(c.firmenname || '').trim() ||
        email;
      const meta = [c.mandantNr, c.firmenname].filter(Boolean).join(' · ');
      if (!map.has(c.id)) map.set(c.id, { id: c.id, name, email, meta });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [crmClients, pendingApprovals]);

  const filteredMandanten = useMemo(() => {
    const q = mandantSearch.trim().toLowerCase();
    if (!q) return clientPool.slice(0, 40);
    return clientPool
      .filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.email.includes(q) ||
          c.meta.toLowerCase().includes(q)
      )
      .slice(0, 40);
  }, [clientPool, mandantSearch]);

  const selectedMandant = selectedMandantId
    ? clientPool.find((c) => c.id === selectedMandantId) || null
    : null;

  const alreadyRegisteredEmails = useMemo(() => {
    const set = new Set<string>();
    registrations.forEach((r) => {
      const e = String(r.email || '').trim().toLowerCase();
      if (e) set.add(e);
    });
    return set;
  }, [registrations]);

  /** Team-E-Mail → bestehende Anmeldung (für Anzeige + Update/Opt-out) */
  const teamRegByEmail = useMemo(() => {
    const map = new Map<string, (typeof registrations)[0]>();
    const teamEmails = new Set(
      adminTeam.map((m) => String(m.email || '').trim().toLowerCase()).filter((e) => e.includes('@'))
    );
    registrations.forEach((r) => {
      const e = String(r.email || '').trim().toLowerCase();
      if (!teamEmails.has(e)) return;
      const prev = map.get(e);
      // Team-Bemerkung bevorzugen, sonst erste Treffer
      if (!prev || String(r.bemerkung || '').toLowerCase() === 'team') {
        map.set(e, r);
      }
    });
    return map;
  }, [adminTeam, registrations]);

  const teamHydrateKey = useMemo(() => {
    return adminTeam
      .map((m) => {
        const email = String(m.email || '').trim().toLowerCase();
        const reg = teamRegByEmail.get(email);
        return `${m.id}:${email}:${JSON.stringify(reg?.anmeldungen || {})}`;
      })
      .join('|');
  }, [adminTeam, teamRegByEmail]);

  useEffect(() => {
    if (teamDirty) return;
    const next: PoolCounts = {};
    adminTeam.forEach((m) => {
      const email = String(m.email || '').trim().toLowerCase();
      const reg = teamRegByEmail.get(email);
      const counts: Record<string, number> = {};
      (config?.tage || []).forEach((t) => {
        counts[t.tagId] = Number(reg?.anmeldungen?.[t.tagId] || 0);
      });
      next[m.id] = counts;
    });
    setTeamCounts(next);
  }, [teamHydrateKey, teamDirty, adminTeam, teamRegByEmail, config?.tage]);

  const patchTeamCount = (memberId: string, tagId: string, n: number) => {
    setTeamDirty(true);
    setTeamCounts((prev) => ({
      ...prev,
      [memberId]: { ...(prev[memberId] || {}), [tagId]: n },
    }));
  };

  const regHydrateKey = useMemo(
    () =>
      registrations
        .map((r) => `${r.id}:${JSON.stringify(r.anmeldungen || {})}`)
        .join('|'),
    [registrations]
  );

  useEffect(() => {
    if (regDirty) return;
    const next: PoolCounts = {};
    registrations.forEach((r) => {
      const counts: Record<string, number> = {};
      (config?.tage || []).forEach((t) => {
        counts[t.tagId] = Number(r.anmeldungen?.[t.tagId] || 0);
      });
      next[r.id] = counts;
    });
    setRegDrafts(next);
  }, [regHydrateKey, regDirty, registrations, config?.tage]);

  const patchRegCount = (regId: string, tagId: string, n: number) => {
    setRegDirty(true);
    setRegDrafts((prev) => ({
      ...prev,
      [regId]: { ...(prev[regId] || {}), [tagId]: n },
    }));
  };

  const handleSaveRegDrafts = async () => {
    setBusy('regs');
    setStatusMsg(null);
    try {
      let changed = 0;
      for (const r of registrations) {
        const counts = regDrafts[r.id] || {};
        const anmeldungen: Record<string, number> = {};
        Object.entries(counts).forEach(([tagId, n]) => {
          if (Number(n) > 0) anmeldungen[tagId] = Number(n);
        });
        const hasHeads = Object.keys(anmeldungen).length > 0;
        if (!hasHeads) {
          await deleteRegistration(r.id);
          changed += 1;
          continue;
        }
        const prevKey = JSON.stringify(r.anmeldungen || {});
        const nextKey = JSON.stringify(anmeldungen);
        if (prevKey === nextKey) continue;
        await updateRegistration(r.id, { anmeldungen });
        changed += 1;
      }
      setRegDirty(false);
      setStatusMsg(
        changed
          ? `${changed} Anmeldung(en) aktualisiert — Tisch-Kopfzahlen sind aktuell.`
          : 'Keine Änderungen an den Anmeldungen.'
      );
    } catch (err) {
      console.error(err);
      setStatusMsg('Anmeldungen speichern fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const handleRemoveRegistration = async (regId: string, label: string) => {
    if (!window.confirm(`„${label}“ komplett aus den Abendessen-Köpfen nehmen?`)) return;
    setBusy(`del_${regId}`);
    try {
      await deleteRegistration(regId);
      setStatusMsg(`„${label}“ entfernt — zählt nicht mehr für die Tische.`);
    } catch (err) {
      console.error(err);
      setStatusMsg('Entfernen fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const saveOneRegistration = async (opts: {
    name: string;
    email: string;
    anmeldungen: Record<string, number>;
    bemerkung?: string;
  }) => {
    const email = opts.email.trim().toLowerCase();
    if (!email.includes('@')) throw new Error('E-Mail fehlt');
    if (!Object.values(opts.anmeldungen).some((n) => Number(n) > 0)) {
      throw new Error('Keine Personenanzahl');
    }
    if (alreadyRegisteredEmails.has(email)) {
      const ok = window.confirm(
        `${opts.name || email} ist bereits angemeldet. Trotzdem erneut hinzufügen?`
      );
      if (!ok) return false;
    }
    await addRegistration({
      seminarId,
      name: opts.name.trim(),
      email,
      anmeldungen: opts.anmeldungen,
      bemerkung: opts.bemerkung,
      isManualEntry: true,
    });
    return true;
  };

  const handleSaveTeamBatch = async () => {
    setBusy('manual');
    setStatusMsg(null);
    try {
      await ensureConfig();
      let changed = 0;
      for (const member of adminTeam) {
        const counts = teamCounts[member.id] || {};
        const anmeldungen: Record<string, number> = {};
        Object.entries(counts).forEach(([tagId, n]) => {
          if (Number(n) > 0) anmeldungen[tagId] = Number(n);
        });
        const email = String(member.email || '').trim().toLowerCase();
        if (!email.includes('@')) continue;

        const existing = teamRegByEmail.get(email);
        const hasHeads = Object.keys(anmeldungen).length > 0;

        if (existing && !hasHeads) {
          // Opt-out: alle Abende auf 0 → Anmeldung entfernen
          await deleteRegistration(existing.id);
          changed += 1;
          continue;
        }
        if (existing && hasHeads) {
          await updateRegistration(existing.id, {
            name: member.name,
            anmeldungen,
            bemerkung: 'Team',
          });
          changed += 1;
          continue;
        }
        if (!existing && hasHeads) {
          await addRegistration({
            seminarId,
            name: member.name,
            email,
            anmeldungen,
            bemerkung: 'Team',
            isManualEntry: true,
          });
          changed += 1;
        }
      }
      if (!changed) {
        setStatusMsg('Keine Änderungen — Personen mit +/- setzen oder Abende auf 0 für Opt-out.');
        return;
      }
      setTeamDirty(false);
      setStatusMsg(`${changed} Team-Eintrag(e) aktualisiert (sichtbar in den Kopfzahlen).`);
    } catch (err) {
      console.error(err);
      setStatusMsg('Team-Speichern fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const handleSaveMandant = async () => {
    if (!selectedMandant) {
      setStatusMsg('Bitte einen Mandanten auswählen.');
      return;
    }
    setBusy('manual');
    setStatusMsg(null);
    try {
      await ensureConfig();
      const anmeldungen: Record<string, number> = {};
      Object.entries(mandantCounts).forEach(([tagId, n]) => {
        if (Number(n) > 0) anmeldungen[tagId] = Number(n);
      });
      const ok = await saveOneRegistration({
        name: selectedMandant.name,
        email: selectedMandant.email,
        anmeldungen,
        bemerkung: selectedMandant.meta ? `Mandant ${selectedMandant.meta}` : 'Mandant',
      });
      if (!ok) return;
      setSelectedMandantId(null);
      setMandantCounts({});
      setMandantSearch('');
      setStatusMsg(`Mandant ${selectedMandant.name} hinzugefügt.`);
    } catch (err) {
      console.error(err);
      setStatusMsg('Mandant speichern fehlgeschlagen — Personenanzahl gesetzt?');
    } finally {
      setBusy(null);
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('manual');
    setStatusMsg(null);
    try {
      await ensureConfig();
      const anmeldungen: Record<string, number> = {};
      Object.entries(manualCounts).forEach(([tagId, n]) => {
        const v = Number(n) || 0;
        if (v > 0) anmeldungen[tagId] = v;
      });
      const ok = await saveOneRegistration({
        name: manualName.trim(),
        email: manualEmail.trim().toLowerCase(),
        anmeldungen,
        bemerkung: manualBemerkung.trim() || 'Extern',
      });
      if (!ok) return;
      setManualName('');
      setManualEmail('');
      setManualBemerkung('');
      setManualCounts({});
      setStatusMsg('Externe Anmeldung hinzugefügt.');
    } catch (err) {
      console.error(err);
      setStatusMsg('Manuelle Anmeldung fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const handleTestMail = async () => {
    setBusy('testmail');
    setStatusMsg(null);
    try {
      const cfg = await ensureConfig();
      const email = testEmail.trim().toLowerCase();
      if (!email.includes('@')) {
        setStatusMsg('Bitte eine Test-E-Mail-Adresse eintragen.');
        return;
      }
      if (!(cfg.tage || []).length) {
        setStatusMsg('Bitte zuerst Abende speichern, dann Test-Mail.');
        return;
      }
      const ok = await enqueueDinnerTestInvitationMail({
        config: cfg,
        email,
        name: 'Test',
      });
      setStatusMsg(
        ok
          ? `Test-Mail an ${email} in die Warteschlange gestellt ([TEST] im Betreff).`
          : 'Test-Mail fehlgeschlagen.'
      );
    } catch (err) {
      console.error(err);
      setStatusMsg('Test-Mail fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const allInviteRecipients = useMemo(
    () =>
      (participants || [])
        .map((p) => ({
          email: String(p.email || '').trim().toLowerCase(),
          name: `${p.vorname || ''} ${p.nachname || ''}`.trim() || p.firma || '',
        }))
        .filter((r) => r.email.includes('@')),
    [participants]
  );

  const pendingInviteRecipients = useMemo(() => {
    const invitationLog = config?.invitationMailSentAtByEmail || {};
    return allInviteRecipients.filter((r) => !invitationLog[r.email]);
  }, [allInviteRecipients, config?.invitationMailSentAtByEmail]);

  const pendingLocationRegs = useMemo(
    () =>
      registrations.filter(
        (r) =>
          !r.locationMailSentAt &&
          Object.values(r.anmeldungen || {}).some((n) => Number(n) > 0)
      ),
    [registrations]
  );

  const sendInvites = async (
    recipients: Array<{ email: string; name?: string }>,
    opts?: { force?: boolean }
  ) => {
    setBusy(opts?.force ? 'invite-force' : 'invite');
    setStatusMsg(null);
    try {
      const cfg = await ensureConfig();
      if (!cfg) return;
      if (!recipients.length) {
        setStatusMsg('Keine Empfänger für Einladung.');
        return;
      }
      const n = await enqueueDinnerInvitationMails({ config: cfg, recipients });
      await markInvitationMailsSent(recipients.map((r) => r.email));
      setStatusMsg(`${n} Einladungs-Mail(s) in die Warteschlange gestellt.`);
    } catch (err) {
      console.error(err);
      setStatusMsg('Einladungs-Versand fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const handleInviteMails = async () => {
    const neu = pendingInviteRecipients;
    const schon = allInviteRecipients.length - neu.length;
    if (!allInviteRecipients.length) {
      setStatusMsg('Keine Teilnehmer-E-Mails im Event vorhanden.');
      return;
    }
    if (!neu.length) {
      const force = window.confirm(
        `Alle ${allInviteRecipients.length} Teilnehmer haben die Einladung bereits erhalten.\n\nTrotzdem erneut an ALLE senden?`
      );
      if (!force) {
        setStatusMsg('Keine neuen Empfänger — Versand abgebrochen.');
        return;
      }
      if (
        !window.confirm(
          `Einladungs-Link erneut an ${allInviteRecipients.length} Teilnehmer senden?\n\n${buildSeminarDinnerPublicUrl(seminarId)}`
        )
      ) {
        return;
      }
      await sendInvites(allInviteRecipients, { force: true });
      return;
    }
    if (
      !window.confirm(
        `Einladungs-Link an ${neu.length} neue Teilnehmer senden` +
          (schon > 0 ? ` (${schon} bereits versorgt werden übersprungen)` : '') +
          `?\n\n${buildSeminarDinnerPublicUrl(seminarId)}`
      )
    ) {
      return;
    }
    await sendInvites(neu);
  };

  const sendLocationMails = async (
    regs: typeof registrations,
    busyKey: string
  ) => {
    setBusy(busyKey);
    setStatusMsg(null);
    try {
      const cfg = config || (await ensureConfig());
      if (!cfg) return;
      if (!regs.length) {
        setStatusMsg('Keine Empfänger für Location-Mail.');
        return;
      }
      const n = await enqueueDinnerLocationDispatchMails({
        config: cfg,
        registrations: regs,
      });
      await markLocationMailsSent(regs.map((r) => r.id));
      setStatusMsg(`${n} Location-Mail(s) in die Warteschlange gestellt.`);
    } catch (err) {
      console.error(err);
      setStatusMsg('Location-Versand fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const daysMissingLocation = (cfgTage: SeminarDinnerDay[]) =>
    (cfgTage || []).filter(
      (t) =>
        !(String(t.restaurantName || '').trim() || String(t.uhrzeit || '').trim())
    );

  const handleLocationDispatch = async () => {
    const cfg = config || (await ensureConfig());
    if (!cfg) return;
    const missing = daysMissingLocation(cfg.tage || []);
    if (missing.length === (cfg.tage || []).length) {
      setStatusMsg(
        'Phase 2: Bitte zuerst Restaurant und/oder Uhrzeit je Abend eintragen — erst dann Details-Mail.'
      );
      return;
    }
    if (missing.length > 0) {
      const ok = window.confirm(
        `Hinweis: Bei ${missing.map((d) => d.label).join(', ')} fehlen noch Restaurant/Uhrzeit.\n\nTrotzdem Details-Mail für die vorhandenen Angaben senden?`
      );
      if (!ok) return;
    }

    const neu = pendingLocationRegs;
    const alle = registrations.filter((r) =>
      Object.values(r.anmeldungen || {}).some((n) => Number(n) > 0)
    );
    if (!alle.length) {
      setStatusMsg('Noch keine Abendessen-Anmeldungen — zuerst Phase 1 (Einladung/Anmeldung).');
      return;
    }
    if (!neu.length) {
      const force = window.confirm(
        `Alle ${alle.length} Angemeldeten haben die Details bereits erhalten.\n\nTrotzdem erneut an ALLE senden?`
      );
      if (!force) {
        setStatusMsg('Keine neuen Empfänger — Versand abgebrochen.');
        return;
      }
      if (!window.confirm(`Location-Details erneut an ${alle.length} Person(en) senden?`)) return;
      await sendLocationMails(alle, 'dispatch-force');
      return;
    }
    const schon = alle.length - neu.length;
    if (
      !window.confirm(
        `Location-Details an ${neu.length} neue Anmeldung(en) senden` +
          (schon > 0 ? ` (${schon} bereits versorgt werden übersprungen)` : '') +
          '?'
      )
    ) {
      return;
    }
    await sendLocationMails(neu, 'dispatch');
  };

  const handleSingleInvite = async (recipient: { email: string; name?: string }) => {
    if (
      !window.confirm(
        `Einladungs-Link an ${recipient.name || recipient.email} (${recipient.email}) senden?`
      )
    ) {
      return;
    }
    await sendInvites([recipient]);
  };

  const handleSingleLocation = async (regId: string) => {
    const reg = registrations.find((r) => r.id === regId);
    if (!reg) return;
    const again = !!reg.locationMailSentAt;
    if (
      !window.confirm(
        again
          ? `Details erneut an ${reg.name} (${reg.email}) senden?`
          : `Details an ${reg.name} (${reg.email}) senden?`
      )
    ) {
      return;
    }
    await sendLocationMails([reg], `loc_${regId}`);
  };

  const tage = config?.tage || [];
  const publicUrl = buildSeminarDinnerPublicUrl(seminarId);
  const totalHeadsCollapsed = useMemo(() => {
    let sum = 0;
    Object.values(headcounts).forEach((n) => {
      sum += Number(n) || 0;
    });
    return sum;
  }, [headcounts]);

  return (
    <section className="mb-8">
      <div className="bg-[#0f172a] rounded-2xl border border-slate-800 shadow-xl overflow-hidden">
        <button
          type="button"
          onClick={togglePanel}
          className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left hover:bg-slate-900/40 transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <UtensilsCrossed className="text-[#E6C299] w-5 h-5 shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-white">Abendessen-Management</h2>
                {unseenDinnerCount > 0 && (
                  <span
                    className="bg-red-500 text-white text-xs font-black px-2 py-0.5 rounded-full tabular-nums"
                    title="Neue Abendessen-Anmeldungen seit letztem Öffnen"
                  >
                    {unseenDinnerCount}
                  </span>
                )}
              </div>
              <p className="text-slate-500 text-xs mt-1 truncate">
                {panelOpen
                  ? 'Zugeklappt halten hält die Event-Akte übersichtlich.'
                  : `${registrations.length} Anmeldung(en) · ${totalHeadsCollapsed} Köpfe gesamt` +
                    (unseenDinnerCount > 0 ? ` · ${unseenDinnerCount} neu` : '')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 text-slate-400">
            {unseenDinnerCount > 0 && !panelOpen && (
              <span className="hidden sm:inline text-[10px] uppercase tracking-widest font-bold text-red-400">
                Neu
              </span>
            )}
            {panelOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </button>

        {panelOpen && (
          <div className="px-5 md:px-6 pb-6 border-t border-slate-800 pt-5">
        <div className="mb-6">
          <p className="text-slate-400 text-sm max-w-3xl leading-relaxed">
            <strong className="text-[#E6C299]">Phase 1:</strong> Abende manuell anlegen (z.B.
            Anreise-Samstag, Seminartag, Abreise) und Einladungs-Link senden — ohne Restaurant.{' '}
            <strong className="text-blue-300">Phase 2:</strong> Später Location/Uhrzeit und
            Details-Mail.
          </p>
          {config && (
            <p className="text-slate-500 text-xs mt-2">
              Öffentlicher Anmelde-Link:{' '}
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E6C299] hover:underline break-all"
              >
                {publicUrl}
              </a>
            </p>
          )}
        </div>

        {statusMsg && (
          <p className="mb-4 text-sm text-slate-300 bg-slate-900/80 border border-slate-800 rounded-lg px-4 py-2">
            {statusMsg}
          </p>
        )}

        {/* ========== PHASE 1 ========== */}
        <div className="mb-8 rounded-xl border border-[#E6C299]/25 bg-[#E6C299]/5 p-4 md:p-5">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-black text-[#E6C299]">
                Phase 1 · Anmeldung
              </p>
              <p className="text-slate-400 text-xs mt-1">
                Abende selbst datieren → Gesamtversand der Einladung → Kopfzahlen. Abendessen sind
                Selbstzahler (Hinweis steht in Mail + Landing). Kein Restaurant nötig für Phase 1.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {!config && (
                <button
                  type="button"
                  onClick={() => { void handleFreischalten(); }}
                  disabled={!!busy}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded text-xs font-bold uppercase tracking-widest transition disabled:opacity-50"
                >
                  {busy === 'init' ? '…' : 'Anmeldung freischalten'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowManual((v) => !v)}
                disabled={!tage.length}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded text-xs font-bold uppercase tracking-widest flex items-center gap-1 transition disabled:opacity-40"
              >
                <Plus className="w-3 h-3" /> Manuelle Anmeldung
              </button>
            </div>
          </div>

          {/* Gesamtversand Einladung — Nachzügler bleiben einzeln unten */}
          {tage.length > 0 && (
            <div className="mb-5 rounded-xl border border-[#E6C299]/40 bg-[#E6C299]/10 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-black text-[#E6C299]">
                  Gesamtversand Einladung
                </p>
                <p className="text-sm text-slate-300 mt-1">
                  {pendingInviteRecipients.length > 0 ? (
                    <>
                      <strong className="text-white">{pendingInviteRecipients.length}</strong> neue
                      Teilnehmer noch ohne Einladung — einmal an alle senden.
                    </>
                  ) : (
                    <>Alle Event-Teilnehmer haben die Einladung bereits erhalten.</>
                  )}
                </p>
                <p className="text-[11px] text-slate-500 mt-1">
                  Nachzügler später einzeln unten nachziehen.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { void handleInviteMails(); }}
                disabled={!!busy || pendingInviteRecipients.length === 0}
                className="px-5 py-3 bg-[#E6C299] hover:bg-white text-black rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition disabled:opacity-40 shrink-0"
              >
                {busy === 'invite' || busy === 'invite-force' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                {pendingInviteRecipients.length > 0
                  ? `Gesamtversand (${pendingInviteRecipients.length})`
                  : 'Nichts offen'}
              </button>
            </div>
          )}

          {/* Test-Mail */}
          <div className="mb-5 flex flex-wrap items-end gap-2 rounded-xl border border-slate-700/80 bg-black/20 p-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                Test-Mail (Phase-1-Einladung)
              </label>
              <input
                type="email"
                className={inputClass}
                placeholder="deine@adresse.de"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => { void handleTestMail(); }}
              disabled={!!busy || !tage.length}
              className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-bold uppercase tracking-widest disabled:opacity-40 flex items-center gap-1"
            >
              {busy === 'testmail' ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Mail className="w-3 h-3" />
              )}{' '}
              Test senden
            </button>
          </div>

        {showManual && (
          <div className="mb-6 p-4 rounded-xl border border-slate-700 bg-black/30 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#E6C299]">
                Köpfe nacherfassen (Team / Mandant / Extern)
              </p>
              <div className="flex gap-1">
                {(
                  [
                    ['team', 'Team'],
                    ['mandant', 'Mandant'],
                    ['extern', 'Extern'],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setManualSource(key)}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border transition ${
                      manualSource === key
                        ? 'bg-[#E6C299] text-black border-[#E6C299]'
                        : 'bg-slate-900 text-slate-400 border-slate-700 hover:border-slate-500'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {manualSource === 'team' && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">
                  Gespeicherte Team-Köpfe bleiben sichtbar. Ändern mit −/+, dann speichern. Abend auf
                  0 = an dem Abend nicht dabei; alle Abende 0 = komplett raus.
                </p>
                {!adminTeam.length ? (
                  <p className="text-slate-600 text-sm italic">Kein Team geladen.</p>
                ) : (
                  <ul className="space-y-2 max-h-80 overflow-y-auto">
                    {adminTeam.map((m) => {
                      const email = String(m.email || '').trim().toLowerCase();
                      const saved = teamRegByEmail.get(email);
                      const counts = teamCounts[m.id] || {};
                      const totalHeads = Object.values(counts).reduce(
                        (s, n) => s + (Number(n) || 0),
                        0
                      );
                      return (
                        <li
                          key={m.id}
                          className={`rounded-xl border p-3 space-y-2 ${
                            saved
                              ? 'border-[#E6C299]/35 bg-[#E6C299]/5'
                              : 'border-slate-800 bg-slate-950/50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-white text-sm font-semibold truncate">{m.name}</p>
                              <p className="text-slate-500 text-xs truncate">
                                {m.email || 'keine E-Mail'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {totalHeads > 0 && (
                                <span className="text-[#E6C299] text-xs font-black tabular-nums">
                                  {totalHeads} Köpfe
                                </span>
                              )}
                              {saved ? (
                                <span className="text-[10px] text-emerald-400 font-bold uppercase">
                                  gespeichert
                                </span>
                              ) : (
                                <span className="text-[10px] text-slate-600 font-bold uppercase">
                                  offen
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-3">
                            {tage.map((t) => {
                              const n = counts[t.tagId] || 0;
                              return (
                                <div key={t.tagId} className="flex items-center gap-2">
                                  <div className="w-28 min-w-0">
                                    <span
                                      className="text-[10px] text-slate-500 block truncate"
                                      title={t.label}
                                    >
                                      {t.label}
                                    </span>
                                    <span
                                      className={`text-[9px] uppercase tracking-wider font-bold ${
                                        n > 0 ? 'text-emerald-500/80' : 'text-slate-600'
                                      }`}
                                    >
                                      {n > 0 ? 'dabei' : 'nicht dabei'}
                                    </span>
                                  </div>
                                  <CountStepper
                                    value={n}
                                    onChange={(v) => patchTeamCount(m.id, t.tagId, v)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { void handleSaveTeamBatch(); }}
                    disabled={busy === 'manual' || !teamDirty}
                    className="px-4 py-2 bg-[#E6C299] text-black rounded-lg text-xs font-black uppercase tracking-widest disabled:opacity-40"
                  >
                    {busy === 'manual' ? 'Speichere…' : 'Änderungen speichern'}
                  </button>
                  {teamDirty && (
                    <button
                      type="button"
                      onClick={() => {
                        setTeamDirty(false);
                        setStatusMsg('Änderungen verworfen — Anzeige zurückgesetzt.');
                      }}
                      className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-white"
                    >
                      Zurücksetzen
                    </button>
                  )}
                </div>
              </div>
            )}

            {manualSource === 'mandant' && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    className={`${inputClass} pl-9`}
                    placeholder="Mandant suchen (Name, Firma, E-Mail, Nr.)…"
                    value={mandantSearch}
                    onChange={(e) => setMandantSearch(e.target.value)}
                  />
                </div>
                {isLoadingPending ? (
                  <p className="text-slate-500 text-sm flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Lade Mandanten…
                  </p>
                ) : (
                  <ul className="max-h-48 overflow-y-auto space-y-1 border border-slate-800 rounded-xl p-1">
                    {filteredMandanten.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedMandantId(c.id);
                            setMandantCounts({});
                          }}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                            selectedMandantId === c.id
                              ? 'bg-[#E6C299]/15 text-[#E6C299] border border-[#E6C299]/40'
                              : 'hover:bg-slate-900 text-slate-300'
                          }`}
                        >
                          <span className="font-medium block truncate">{c.name}</span>
                          <span className="text-xs text-slate-500 block truncate">
                            {c.email}
                            {c.meta ? ` · ${c.meta}` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                    {!filteredMandanten.length && (
                      <li className="text-slate-600 text-sm italic px-3 py-2">Keine Treffer.</li>
                    )}
                  </ul>
                )}
                {selectedMandant && (
                  <div className="rounded-xl border border-slate-700 p-3 space-y-3">
                    <p className="text-sm text-white">
                      Gewählt: <strong>{selectedMandant.name}</strong>
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {tage.map((t) => (
                        <div key={t.tagId} className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-500 w-24 truncate">{t.label}</span>
                          <CountStepper
                            value={mandantCounts[t.tagId] || 0}
                            onChange={(n) =>
                              setMandantCounts((prev) => ({ ...prev, [t.tagId]: n }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => { void handleSaveMandant(); }}
                      disabled={busy === 'manual'}
                      className="px-4 py-2 bg-[#E6C299] text-black rounded-lg text-xs font-black uppercase tracking-widest disabled:opacity-50"
                    >
                      {busy === 'manual' ? 'Speichere…' : 'Mandant hinzufügen'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {manualSource === 'extern' && (
              <form
                onSubmit={(e) => { void handleManualSubmit(e); }}
                className="space-y-3"
              >
                <p className="text-xs text-slate-500">
                  Kein Mandant / kein Team — Name &amp; E-Mail manuell.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    className={inputClass}
                    placeholder="Name"
                    value={manualName}
                    onChange={(e) => setManualName(e.target.value)}
                    required
                  />
                  <input
                    className={inputClass}
                    type="email"
                    placeholder="E-Mail"
                    value={manualEmail}
                    onChange={(e) => setManualEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="flex flex-wrap gap-3">
                  {tage.map((t) => (
                    <div key={t.tagId} className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-500 w-24 truncate">{t.label}</span>
                      <CountStepper
                        value={manualCounts[t.tagId] || 0}
                        onChange={(n) =>
                          setManualCounts((prev) => ({ ...prev, [t.tagId]: n }))
                        }
                      />
                    </div>
                  ))}
                </div>
                <input
                  className={inputClass}
                  placeholder="Bemerkung (optional)"
                  value={manualBemerkung}
                  onChange={(e) => setManualBemerkung(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={busy === 'manual'}
                  className="px-4 py-2 bg-[#E6C299] text-black rounded-lg text-xs font-black uppercase tracking-widest disabled:opacity-50"
                >
                  {busy === 'manual' ? 'Speichere…' : 'Extern hinzufügen'}
                </button>
              </form>
            )}
          </div>
        )}

        {isLoading && !config ? (
          <div className="flex items-center gap-2 text-slate-500 py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Laden…
          </div>
        ) : !config ? (
          <p className="text-slate-500 text-sm italic py-2">
            Noch nicht freigeschaltet. Danach trägst du die Abende manuell mit Datum ein (z.B.
            Anreiseabend, Seminartag, Abreiseabend).
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                Abende manuell (Datum + Bezeichnung)
              </p>
              {tageDraft.length === 0 ? (
                <p className="text-slate-600 text-sm italic">Noch keine Abende — unten hinzufügen.</p>
              ) : (
                tageDraft.map((day, index) => (
                  <div
                    key={day.tagId}
                    className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end bg-black/30 border border-slate-800 rounded-xl p-3"
                  >
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                        Datum
                      </label>
                      <input
                        type="date"
                        className={inputClass}
                        value={day.datum?.slice(0, 10) || ''}
                        onChange={(e) =>
                          handlePatchDinnerDay(day.tagId, { datum: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                        Bezeichnung
                      </label>
                      <input
                        type="text"
                        className={inputClass}
                        value={day.label}
                        onChange={(e) =>
                          handlePatchDinnerDay(day.tagId, { label: e.target.value })
                        }
                        placeholder={`z.B. Abend ${index + 1} / Anreise`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDinnerDay(day.tagId)}
                      className="px-3 py-2 text-red-400 hover:bg-red-900/30 rounded-lg border border-red-900/40"
                      title="Abend entfernen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleAddDinnerDay}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded text-xs font-bold uppercase tracking-widest flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Abend hinzufügen
                </button>
                <button
                  type="button"
                  onClick={() => { void handleSaveTage(); }}
                  disabled={!!busy}
                  className="px-3 py-1.5 bg-[#E6C299] hover:bg-white text-black rounded text-xs font-black uppercase tracking-widest disabled:opacity-50"
                >
                  {busy === 'tage' ? '…' : 'Abende speichern'}
                </button>
              </div>
            </div>

            {tage.length > 0 && (
              <div className="space-y-4 pt-2 border-t border-slate-800 mt-4">
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                    Tisch-Übersicht (aktuelle Köpfe)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {tage.map((day) => {
                      const draftTotal = registrations.reduce((sum, r) => {
                        const fromDraft = regDrafts[r.id]?.[day.tagId];
                        const n =
                          typeof fromDraft === 'number'
                            ? fromDraft
                            : Number(r.anmeldungen?.[day.tagId] || 0);
                        return sum + (n > 0 ? n : 0);
                      }, 0);
                      const saved = headcounts[day.tagId] || 0;
                      return (
                        <div
                          key={day.tagId}
                          className="rounded-xl border border-slate-800 bg-black/40 px-4 py-3 min-w-[140px]"
                        >
                          <p className="text-[10px] text-slate-500 uppercase tracking-widest truncate">
                            {day.label}
                          </p>
                          <p className="text-2xl font-black text-[#E6C299] tabular-nums">
                            {regDirty ? draftTotal : saved}
                          </p>
                          <p className="text-[10px] text-slate-600">
                            {regDirty && draftTotal !== saved ? `gespeichert: ${saved}` : 'Köpfe'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                    Anmeldungen anpassen ({registrations.length})
                  </p>
                  <p className="text-xs text-slate-500 mb-3">
                    Beliebig ändern: Abend auf 0 = nicht dabei, alle 0 oder „Raus“ = zählt nicht mehr
                    für die Tische. Danach speichern.
                  </p>
                  {!registrations.length ? (
                    <p className="text-slate-600 text-sm italic">Noch keine Anmeldungen.</p>
                  ) : (
                    <ul className="space-y-2 max-h-96 overflow-y-auto">
                      {registrations.map((r) => {
                        const counts = regDrafts[r.id] || {};
                        const total = Object.values(counts).reduce(
                          (s, n) => s + (Number(n) || 0),
                          0
                        );
                        return (
                          <li
                            key={r.id}
                            className="rounded-xl border border-slate-800 bg-slate-950/50 p-3 space-y-2"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-white text-sm font-semibold truncate">
                                  {r.name || '—'}
                                  {r.isManualEntry && (
                                    <span className="ml-2 text-[10px] text-amber-400 uppercase font-bold">
                                      {r.bemerkung === 'Team' ? 'Team' : 'manuell'}
                                    </span>
                                  )}
                                </p>
                                <p className="text-slate-500 text-xs truncate">{r.email}</p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-[#E6C299] text-xs font-black tabular-nums">
                                  {total} Köpfe
                                </span>
                                <button
                                  type="button"
                                  disabled={!!busy}
                                  onClick={() => {
                                    void handleRemoveRegistration(r.id, r.name || r.email);
                                  }}
                                  className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-red-400 border border-red-900/40 rounded-lg hover:bg-red-900/30 disabled:opacity-40"
                                  title="Komplett aus Kopfzahl nehmen"
                                >
                                  Raus
                                </button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-3">
                              {tage.map((t) => {
                                const n = counts[t.tagId] || 0;
                                return (
                                  <div key={t.tagId} className="flex items-center gap-2">
                                    <div className="w-28 min-w-0">
                                      <span
                                        className="text-[10px] text-slate-500 block truncate"
                                        title={t.label}
                                      >
                                        {t.label}
                                      </span>
                                      <span
                                        className={`text-[9px] uppercase tracking-wider font-bold ${
                                          n > 0 ? 'text-emerald-500/80' : 'text-slate-600'
                                        }`}
                                      >
                                        {n > 0 ? 'dabei' : 'nicht dabei'}
                                      </span>
                                    </div>
                                    <CountStepper
                                      value={n}
                                      onChange={(v) => patchRegCount(r.id, t.tagId, v)}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {registrations.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => { void handleSaveRegDrafts(); }}
                        disabled={busy === 'regs' || !regDirty}
                        className="px-4 py-2 bg-[#E6C299] text-black rounded-lg text-xs font-black uppercase tracking-widest disabled:opacity-40"
                      >
                        {busy === 'regs' ? 'Speichere…' : 'Kopfzahlen speichern'}
                      </button>
                      {regDirty && (
                        <button
                          type="button"
                          onClick={() => {
                            setRegDirty(false);
                            setStatusMsg('Änderungen verworfen.');
                          }}
                          className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-white"
                        >
                          Zurücksetzen
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {tage.length > 0 && (
          <div className="mt-5 pt-4 border-t border-[#E6C299]/15">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
              Nachzügler · nur Einzelversand ({pendingInviteRecipients.length} offen)
            </p>
            <p className="text-xs text-slate-500 mb-3">
              Für Spätanmelder nach dem Gesamtversand — hier bewusst nur einzeln.
            </p>
            {pendingInviteRecipients.length === 0 ? (
              <p className="text-slate-600 text-sm italic">
                Keine offenen Einladungen.
              </p>
            ) : (
              <ul className="space-y-2">
                {pendingInviteRecipients.map((r) => (
                  <li
                    key={r.email}
                    className="flex items-center justify-between gap-3 text-sm bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-white truncate">{r.name || '—'}</p>
                      <p className="text-slate-500 text-xs truncate">{r.email}</p>
                    </div>
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() => { void handleSingleInvite(r); }}
                      className="shrink-0 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded text-[10px] font-bold uppercase tracking-widest disabled:opacity-50"
                    >
                      Einzeln senden
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        </div>

        {/* ========== PHASE 2 ========== */}
        <div className="rounded-xl border border-blue-500/25 bg-blue-950/20 p-4 md:p-5">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-black text-blue-300">
                Phase 2 · Location &amp; Zeiten
              </p>
              <p className="text-slate-400 text-xs mt-1">
                Erst wenn Restaurant &amp; Uhrzeit stehen: eintragen und Details-Mail an
                Angemeldete. Unabhängig von Phase 1.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { void handleLocationDispatch(); }}
              disabled={!!busy || !registrations.length}
              className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white border border-blue-600/40 rounded text-xs font-bold uppercase tracking-widest flex items-center gap-1 transition disabled:opacity-40"
            >
              {busy === 'dispatch' || busy === 'dispatch-force' ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <MapPin className="w-3 h-3" />
              )}{' '}
              Details-Mail an Neue
              {pendingLocationRegs.length > 0 ? ` (${pendingLocationRegs.length})` : ''}
            </button>
          </div>

          {!tage.length ? (
            <p className="text-slate-600 text-sm italic">
              Zuerst Phase 1 freischalten — danach kannst du hier Locations nachpflegen.
            </p>
          ) : (
            <div className="space-y-3">
              {tage.map((day) => (
                <DayCard
                  key={`loc_${day.tagId}`}
                  mode="phase2"
                  day={day}
                  total={headcounts[day.tagId] || 0}
                  open
                  dayRegs={[]}
                  busy={busy === `day_${day.tagId}`}
                  onToggle={() => undefined}
                  onSave={(patched) => { void handleSaveDay(patched); }}
                  onDeleteReg={() => undefined}
                />
              ))}
            </div>
          )}

          {tage.length > 0 && (
            <div className="mt-5 pt-4 border-t border-blue-500/15 space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                  Details Nachzügler ({pendingLocationRegs.length} offen)
                </p>
                {pendingLocationRegs.length === 0 ? (
                  <p className="text-slate-600 text-sm italic">
                    Keine offenen Location-Mails (oder noch keine Anmeldungen).
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {pendingLocationRegs.map((r) => (
                      <li
                        key={r.id}
                        className="flex items-center justify-between gap-3 text-sm bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="text-white truncate">{r.name}</p>
                          <p className="text-slate-500 text-xs truncate">{r.email}</p>
                        </div>
                        <button
                          type="button"
                          disabled={!!busy}
                          onClick={() => { void handleSingleLocation(r.id); }}
                          className="shrink-0 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white border border-blue-600/40 rounded text-[10px] font-bold uppercase tracking-widest disabled:opacity-50"
                        >
                          {busy === `loc_${r.id}` ? '…' : 'Details'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {registrations.some((r) => r.locationMailSentAt) && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                    Details erneut senden
                  </p>
                  <ul className="space-y-2">
                    {registrations
                      .filter(
                        (r) =>
                          r.locationMailSentAt &&
                          Object.values(r.anmeldungen || {}).some((n) => Number(n) > 0)
                      )
                      .map((r) => (
                        <li
                          key={`resent_${r.id}`}
                          className="flex items-center justify-between gap-3 text-sm bg-black/20 border border-slate-800/80 rounded-lg px-3 py-2"
                        >
                          <div className="min-w-0">
                            <p className="text-slate-300 truncate">{r.name}</p>
                            <p className="text-slate-600 text-xs truncate">
                              zuletzt {new Date(r.locationMailSentAt!).toLocaleString('de-DE')}
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={!!busy}
                            onClick={() => { void handleSingleLocation(r.id); }}
                            className="shrink-0 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[10px] font-bold uppercase tracking-widest disabled:opacity-50"
                          >
                            Erneut
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
          </div>
        )}
      </div>
    </section>
  );
};

const DayCard: React.FC<{
  mode: 'phase1' | 'phase2';
  day: SeminarDinnerDay;
  total: number;
  open: boolean;
  busy: boolean;
  dayRegs: Array<{
    id: string;
    name: string;
    email: string;
    anmeldungen: Record<string, number>;
    isManualEntry: boolean;
    bemerkung?: string;
  }>;
  onToggle: () => void;
  onSave: (day: SeminarDinnerDay) => void;
  onDeleteReg: (id: string) => void;
}> = ({ mode, day, total, open, busy, dayRegs, onToggle, onSave, onDeleteReg }) => {
  const [draft, setDraft] = useState(day);
  useEffect(() => {
    setDraft(day);
  }, [day]);

  const hasLocation =
    !!(String(day.restaurantName || '').trim() || String(day.uhrzeit || '').trim());

  if (mode === 'phase2') {
    return (
      <div className="border border-slate-800 rounded-xl overflow-hidden bg-black/30 p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-white font-semibold text-sm">{day.label}</p>
            <p className="text-slate-500 text-xs">{day.datum || '—'}</p>
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-widest ${hasLocation ? 'text-emerald-400' : 'text-amber-400'}`}>
            {hasLocation ? 'Location gesetzt' : 'Noch offen'}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Restaurant
            </label>
            <input
              className={inputClass}
              value={draft.restaurantName || ''}
              onChange={(e) => setDraft({ ...draft, restaurantName: e.target.value })}
              placeholder="später eintragen"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Adresse / Treffpunkt
            </label>
            <input
              className={inputClass}
              value={draft.adresseTreffpunkt || ''}
              onChange={(e) => setDraft({ ...draft, adresseTreffpunkt: e.target.value })}
              placeholder="später eintragen"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Uhrzeit
            </label>
            <input
              className={inputClass}
              value={draft.uhrzeit || ''}
              onChange={(e) => setDraft({ ...draft, uhrzeit: e.target.value })}
              placeholder="z.B. 19:30"
            />
          </div>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => onSave(draft)}
          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded text-xs font-bold uppercase tracking-widest disabled:opacity-50"
        >
          {busy ? '…' : 'Location speichern'}
        </button>
      </div>
    );
  }

  return (
    <div className="border border-slate-800 rounded-xl overflow-hidden bg-black/30">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-900/50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Users className="w-4 h-4 text-[#E6C299] shrink-0" />
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm truncate">{day.label}</p>
            <p className="text-slate-500 text-xs">{day.datum || '—'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[#E6C299] font-black text-sm tabular-nums">
            {total} {total === 1 ? 'Kopf' : 'Köpfe'}
          </span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-800 space-y-3 pt-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Teilnehmer ({dayRegs.length})
          </p>
          {dayRegs.length === 0 ? (
            <p className="text-slate-600 text-sm italic">Noch keine Anmeldungen für diesen Abend.</p>
          ) : (
            <ul className="space-y-2">
              {dayRegs.map((r) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-3 text-sm bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-white truncate">
                      {r.name}{' '}
                      {r.isManualEntry && (
                        <span className="text-[10px] text-amber-400 uppercase font-bold ml-1">
                          manuell
                        </span>
                      )}
                    </p>
                    <p className="text-slate-500 text-xs truncate">{r.email}</p>
                    {r.bemerkung ? (
                      <p className="text-slate-600 text-xs truncate">{r.bemerkung}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[#E6C299] font-bold tabular-nums">
                      {r.anmeldungen[day.tagId]} Pers.
                    </span>
                    <button
                      type="button"
                      onClick={() => onDeleteReg(r.id)}
                      className="p-1.5 text-red-400 hover:bg-red-900/30 rounded"
                      title="Löschen"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SeminarDinnerManager;
